<template>
    <div v-if="isDataLoaded" class="ticket-item">
        <button
            class="ticket-item__group"
            v-show="ticketType === TicketType.KOMBI"
            @click.capture="handleClickCapture"
            @click="groupToggle"
        >
            <span class="ticket-item__group__inner">{{ groupName }}</span>
            <SvgIcon name="dropdown-down" :size="16" />
        </button>
        <div
            v-show="isGroupSelectActive && isDesktop"
            v-on-click-outside="groupClickOutside"
            class="ticket-item__group-select"
        >
            <div class="ticket-item__group-select__header">
                Skupiny
                <button @click="groupClose">
                    <SvgIcon name="close-big" />
                </button>
            </div>
            <div class="ticket-item__group-select__groups">
                <button
                    class="ticket-item__group-select__group"
                    :class="{
                        'ticket-item__group-select__group--active': isGroupActive(groupNumber),
                        'ticket-item__group-select__group--tutovka': groupNumber === KombiGroup.T,
                    }"
                    v-for="groupNumber in groups"
                    :key="groupNumber"
                    @click="selectGroupClick(groupNumber)"
                >
                    {{ KombiGroup[groupNumber] }}
                    <Tooltip text="Tutovka" v-if="groupNumber === KombiGroup.T">
                        <SvgIcon name="help-2" :size="16" />
                    </Tooltip>
                </button>
            </div>
        </div>
        <BottomSheet
            v-if="!isDesktop"
            v-model:open="isGroupSelectActive"
            heading="Zařazení do skupiny"
            :items="bottomSheetGroups"
            :active-value="ticketStore.kombiGroupNumberByOutcomeId(outcomeId)?.toString()"
            @select-value="($event) => selectGroupClick(Number($event))"
        />
        <div class="ticket-item__wrap">
            <div class="ticket-item__top">
                <span class="ticket-item__date">
                    {{ date }}
                    <MatchIcon class="icon-shadow" :sport-name="categoryName" size="12" />
                </span>
                <button class="ticket-item__remove" @click="ticketStore.removeOutcome(outcomeId)">
                    <SvgIcon name="close" :size="24" />
                </button>
            </div>
            <RouterLink :to="eventLink" class="ticket-item__title">
                <Tooltip>
                    <template #tooltip>
                        <MatchIcon :sport-name="categoryName" size="12" />
                        {{ tooltipText }}
                    </template>
                    {{ eventName }}
                </Tooltip>
                <button
                    class="ticket-item__remove ticket-item__remove--dt"
                    @click.prevent="ticketStore.removeOutcome(outcomeId)"
                >
                    <SvgIcon name="close-big" :size="16" />
                </button>
            </RouterLink>
            <div v-if="rate !== '\xA0' && itemSubtitle !== '\xA0'" class="ticket-item__score">
                <span class="ticket-item__subtitle" v-html="itemSubtitle" />
                <div class="ticket-item__course">
                    <SvgIcon v-if="rateState === RateEnum.HIGHER" name="kurz-up" :size="16" />
                    <SvgIcon v-else-if="rateState === RateEnum.LOWER" name="kurz-down" :size="16" />
                    <div v-else style="width: 16px" />
                    {{ rate }}
                </div>
            </div>
            <div v-else class="ticket-item__score-loader">
                <SazkaSpinLoader :size="16" center />
            </div>
            <span class="ticket-item__live" v-if="event?.liveNow">
                <SvgIcon name="live" />
                <ScorePreview :event :key="`score-preview-ticket_${event.id}`" />
            </span>
            <!-- Unable to detect events which ended properly... only by presumption that if it'll not be returned by endpoint event is over -->
            <div class="ticket-item__overlay" v-if="isEventOver || isEventStarted || isSuspended">
                <button class="ticket-item__overlay__close" @click.stop.prevent="ticketStore.removeOutcome(outcomeId)">
                    <SvgIcon :name="isDesktop ? 'close-big' : 'close'" :size="isDesktop ? 16 : 24" />
                </button>
                {{ overlayText }}
            </div>
        </div>
        <div class="ticket-item__error" v-if="isError" />
    </div>
    <div v-else class="ticket-item">
        <div :class="`ticket-item__loader${isLiveRoute ? '--live' : ''}`">
            <SazkaSpinLoader :size="32" center />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, ref, watch } from "vue";
import { computedAsync } from "@vueuse/core";
import { handleClickCapture, useTicketStore } from "@/ticket/stores/use-ticket-store";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { usePrematchStore } from "@/prematch/stores/prematch-sports";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";
import MatchIcon from "@/shared/components/atoms/MatchIcon.vue";
import Tooltip from "@/shared/components/atoms/Tooltip.vue";
import ScorePreview from "@/live/components/molecules/ScorePreview.vue";
import BottomSheet from "@/shared/components/organisms/BottomSheet.vue";
import { getPrematchDate } from "@/shared/composables/use-event-score-and-status";
import useUtils from "@/shared/composables/use-utils";
import { TicketType, KombiGroup } from "@/shared/model/ticket";
import { RateEnum } from "@/shared/model/odds-button";
import { vOnClickOutside } from "@vueuse/components";
import { cloneDeep as _cloneDeep, sortBy as _sortBy } from "lodash";
import { useRouter } from "vue-router";
import DrilldownNodeUtils from "@/shared/utils/drilldown-node-utils";
import { DRILLDOWN_SPORT_LEVEL_NUMBER } from "@/shared/utils/constants";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import SazkaSpinLoader from "@/shared/components/atoms/SazkaSpinLoader.vue";
import useSazkaSnackbar from "@/shared/composables/use-sazka-snackbar";
import { logCustomException } from "@/shared/utils/logger/use-app-logger";

const props = defineProps<{
    outcomeId: string;
}>();

const router = useRouter();
const ticketStore = useTicketStore();
const drilldownNodesStore = useDrilldownNodesStore();
const prematchStore = usePrematchStore();

const { formatRate } = useUtils();

const ticketType = computed(() => ticketStore.ticketType);

const { isDesktop } = useIsBreakpoint();

const isGroupSelectActive = ref(false);

const groupToggle = () => {
    isGroupSelectActive.value = !isGroupSelectActive.value;
};

const groupClose = () => {
    isGroupSelectActive.value = false;
};

const groupClickOutside = [groupClose, { ignore: [".ticket-item__group"] }] as any;

const isGroupActive = (groupNumber: number) => {
    return ticketStore.kombiGroupNumberByOutcomeId(props.outcomeId) === groupNumber;
};

const selectGroupClick = (groupNumber: number) => {
    // Prevent set the same group because it will trigger reshuffle inside the group and outcome will be the last in the group (this is unwelcome behavior)
    if (!isGroupActive(groupNumber)) {
        ticketStore.setKombiGroupMapping(props.outcomeId, groupNumber);
    }
    groupClose();
};

const groupName = computed(() => {
    return KombiGroup[ticketStore.kombiGroupNumberByOutcomeId(props.outcomeId)];
});

const groups = computed(() => {
    return ticketStore.availableKombiGroupsByOutcomeId(props.outcomeId);
});

const bottomSheetGroups = computed(() =>
    _sortBy(ticketStore.availableKombiGroupsByOutcomeId(props.outcomeId), (o) =>
        o === KombiGroup.T ? Infinity : o,
    ).map((group) => ({ name: group === KombiGroup.T ? "Tutovka" : KombiGroup[group], value: group.toString() })),
);

const event = computed(() => ticketStore.getEventByOutcomeId(props.outcomeId));
const outcome = computed(() => ticketStore.ticketOutcomes.find((to) => to.id === props.outcomeId));
const drilldownNodes = computedAsync(
    async () => await drilldownNodesStore.getDrilldownNodesById(event.value.competitionDrilldownTagId),
);
const isDataLoaded = computed(() => event.value && drilldownNodes.value?.length > 0);
// Remove outcome if we'll not get data in given time limit to prevent infinite loader
let timeoutId = setTimeout(() => {
    if (isDataLoaded.value) return;
    logCustomException("ticket-item", "Data retrieval timed out", {
        outcomeId: props.outcomeId,
        event: _cloneDeep(event.value),
        outcome: _cloneDeep(outcome.value),
        drilldownNodes: _cloneDeep(drilldownNodes.value),
    });
    ticketStore.removeOutcome(props.outcomeId);
    useSazkaSnackbar().createSnackbar({
        message:
            "Nepovedlo se načíst informace o sázkové přiležitosti a byla odebrána z tiketu. Zkuste to prosím znovu.",
        type: "error",
        duration: 4_000,
        icon: "attention",
    });
    timeoutId = null;
}, 30_000);
const unwatch = watch(isDataLoaded, (newValue) => {
    if (!newValue || !timeoutId) return;
    clearTimeout(timeoutId);
    timeoutId = null;
    unwatch();
});
const outcomeName = computed(() => outcome.value?.name);
const eventName = computed(() => event.value?.name || "\xA0");
const marketName = computed(() => event.value?.markets?.find((m) => m.id === outcome.value?.marketId)?.name);
const categoryName = computed(
    () => drilldownNodes.value?.find((node) => node.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER)?.name,
);

const itemSubtitle = computed(() => {
    const marketN = marketName.value || "";
    const outcomeN = outcomeName.value || "";
    const hlow = outcome.value?.prices?.[0]?.handicapLow;
    const hhigh = outcome.value?.prices?.[0]?.handicapHigh;
    const handicapString = hlow == hhigh ? hlow : `${hlow}/${hhigh}`;

    if (marketN || outcomeN) {
        return `${marketN}${marketN ? ":" : ""}&nbsp;<strong>${outcomeN} ${handicapString || ""}</strong>`;
    } else {
        return "\xA0";
    }
});

const date = computed(() => {
    const date = getPrematchDate(event.value);

    return event.value ? `${date.startDate} | ${date.startTime}` : "";
});

const tooltipText = computed(() => {
    return `${date.value} | ${drilldownNodes.value?.length > 0 ? DrilldownNodeUtils.getCategoryName(drilldownNodes.value) : ""}`;
});

const price = computed(() => outcome.value?.prices?.[0]?.decimal || 0);
const rate = computed(() => (Number(price.value) > 0 ? formatRate(Number(price.value)) : "\xA0"));

const rateState = ref(RateEnum.EQUAL);

const delayedRateStateReset = () => setTimeout(() => (rateState.value = RateEnum.EQUAL), 5000);

watch(price, (newValue, oldValue) => {
    if (!oldValue) return;
    if (newValue > oldValue) {
        rateState.value = RateEnum.HIGHER;
        delayedRateStateReset();
    } else if (newValue < oldValue) {
        rateState.value = RateEnum.LOWER;
        delayedRateStateReset();
    } else {
        rateState.value = RateEnum.EQUAL;
    }
});

const suspendedOutcomeIds: ComputedRef<string[]> = computed(
    () =>
        (ticketStore.errors?.find((err) => err.errorId === "OUTCOME_SUSPENDED")?.errorRefs?.map((i) => i?.[0]) ||
            []) as string[],
);
const resultedOutcomeIds: ComputedRef<string[]> = computed(
    () =>
        (ticketStore.errors?.find((err) => err.errorId === "OUTCOME_RESULTED")?.errorRefs?.map((i) => i?.[0]) ||
            []) as string[],
);
const startedOutcomeIds: ComputedRef<string[]> = computed(
    () =>
        (ticketStore.errors?.find((err) => err.errorId === "EVENT_STARTED")?.errorRefs?.map((i) => i?.[0]) ||
            []) as string[],
);

const errorOutcomeIds: ComputedRef<string[]> = computed(
    () =>
        (ticketStore.errors
            ?.filter((err) => !["OUTCOME_SUSPENDED", "OUTCOME_RESULTED", "EVENT_STARTED"].includes(err.errorId))
            ?.flatMap((err) => err.errorRefs)
            ?.map((i) => i?.[0]) || []) as string[],
);

const isSuspended: ComputedRef<boolean> = computed(() => suspendedOutcomeIds.value.includes(props.outcomeId));
const isEventOver: ComputedRef<boolean> = computed(() => resultedOutcomeIds.value.includes(props.outcomeId));
const isEventStarted: ComputedRef<boolean> = computed(() => startedOutcomeIds.value.includes(props.outcomeId));
const isError: ComputedRef<boolean> = computed(() => errorOutcomeIds.value.includes(props.outcomeId));

const overlayText = computed(() => {
    if (isEventOver.value) return "Zápas skončil";
    // We should behave to "EVENT_STARTED" like to "OUTCOME_SUSPENDED" (defined by business)
    else if (isSuspended.value || isEventStarted.value) return "Pozastaveno";
    else return "";
});

const eventLink = computed(() => {
    if (!event.value) return "";

    const eventLeague = prematchStore.getLeagueById(event.value.competitionDrilldownTagId);

    if (!eventLeague) return "";

    const routeName = event.value.liveNow ? "LiveEventDetail" : "PrematchEventDetail";
    const params = event.value.liveNow
        ? { id: event.value.id }
        : { sportAndCountryId: eventLeague.sportId, leagueId: eventLeague.id, eventId: event.value.id };

    return { name: routeName, params: params };
});

const isLiveRoute: ComputedRef<boolean> = computed(() =>
    ["Live", "LiveEventDetail"].includes(router.currentRoute.value?.name?.toString()),
);
</script>
